/**
 * Risk profile reveal — error screen after compute fails (Review resolve overlay).
 *
 * Belongs here: error chrome (red gradient, warning asset, retry).
 * Does not belong here: fetch/retry orchestration (see StrategyResolveOverlay).
 */
import { PrimaryButton } from '../../../../../components/execution/PrimaryButton'
import { getBottomPadding } from '../../../../../utils/sessionStorage'
import { EventNames } from '../../../../../types/misc.types'
import { lightImpactVibrate, trackEvent } from '../../../../../utils/appBridge'
import { expertReviewSafeAreaTopStyle } from '../../shared/expertReviewSafeArea'
import { ExpertReviewTopBar } from '../../shared/components/ExpertReviewTopBar'
import { ExpertReviewEventIds, ExpertReviewScreen } from '../../analytics/expertReviewEvents'

const RISK_PROFILE_BG_PATTERN_SRC = '/images/allocation/risk-profiles-v2/bg-pattern.svg'
/** Shared filled warning triangle (mf-l2 alert asset). */
const RISK_PROFILE_ERROR_ASSET_SRC = '/images/mf-l2/alert-triangle.svg'

type RiskProfileErrorPageProps = {
  onBack: () => void
  onRetry: () => void
}

/** Failed risk-profile lookup — mirrors ready-page layout with error tone. */
export function RiskProfileErrorPage({
  onBack,
  onRetry
}: RiskProfileErrorPageProps) {
  return (
    <div
      className="relative flex h-screen flex-col min-h-0 overflow-hidden animate-fade-250"
      style={{
        ...expertReviewSafeAreaTopStyle(),
        paddingBottom: `calc(${getBottomPadding()}px + 16px)`,
        background: 'linear-gradient(180deg, #FFB4A8 0%, #FFF 100%)'
      }}
      role="alert"
    >
      <img
        src={RISK_PROFILE_BG_PATTERN_SRC}
        alt=""
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-0 z-0 h-auto w-full max-w-none -translate-x-1/2 object-cover opacity-40 mix-blend-soft-light
        [@media(min-height:680px)]:w-[140%] [@media(min-height:780px)]:w-[180%]"
      />

      <div className="relative z-10 shrink-0 px-5">
        <ExpertReviewTopBar
          onBack={onBack}
          menu="strategy"
          analyticsScreen={ExpertReviewScreen.StrategyReview}
          testLocatorBack="strategy-risk-profile-error-back"
          testLocatorMenu="strategy-risk-profile-error-menu"
        />
      </div>

      <div className="relative z-10 flex flex-1 min-h-0 flex-col items-center overflow-auto no-scrollbar px-5">
        <div className="my-auto flex w-full max-w-[303px] flex-col items-center gap-10">
          <div className="flex h-[124px] w-[124px] items-center justify-center" aria-hidden>
            <img
              src={RISK_PROFILE_ERROR_ASSET_SRC}
              alt=""
              className="h-[88px] w-[88px] object-contain"
            />
          </div>

          <div className="flex w-full flex-col items-center gap-3 text-center">
            <h1
              className="text-[28px] font-[500] leading-[130%] text-[#232323]"
              test-locator="strategy-risk-profile-error-title"
            >
              Unable to load your risk profile
            </h1>
            <p
              className="text-[14px] font-[400] leading-[150%] text-[rgba(0,0,0,0.7)]"
              test-locator="strategy-risk-profile-error-description"
            >
              Something went wrong while calculating your allocation strategy. Please try again.
            </p>
          </div>
        </div>
      </div>

      <div className="relative z-10 shrink-0 flex items-end pt-1 w-full px-5">
        <PrimaryButton
          testLocator="strategy-risk-profile-error-retry"
          onClick={() => {
            lightImpactVibrate()
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                ScreenName: ExpertReviewScreen.StrategyReview,
                eventId: ExpertReviewEventIds.RiskRetry,
                buttonLabel: 'Retry'
              }
            })
            onRetry()
          }}
          label="Retry"
          primaryButtonClassName="w-full !h-[56px] !font-[500] !rounded-[16px] !bg-[#232323]"
        />
      </div>
    </div>
  )
}
